import { useCallback, useEffect, useRef, useState } from "react";
import { htmlAMarcas, marcasAHtml } from "../../utils/textoRico";
import "./EditorTexto.css";

/**
 * Editor de texto con formato, sin librerias.
 *
 * Se ve lo que se obtiene: la negrita aparece en negrita mientras se
 * escribe, no como marcas. Por dentro es un contentEditable, pero su HTML
 * nunca se guarda: en cada cambio se serializa a texto plano con marcas
 * (**negrita**, *cursiva*, - vineta, 1. numerada). Todo lo que no sea una
 * de esas cosas se descarta en la conversion, asi que no hay HTML de origen
 * desconocido viajando a la base.
 *
 * Se usa document.execCommand porque no existe reemplazo estandar para
 * aplicar formato sobre una seleccion. Esta marcado como obsoleto pero
 * todos los navegadores actuales lo soportan.
 *
 * Lo que se puede hacer:
 *   Ctrl+B / Ctrl+I      negrita y cursiva
 *   escribir "- " o "* " al empezar una linea    abre una lista de vinetas
 *   escribir "1. " o "1) " al empezar una linea  abre una lista numerada
 *   Enter                sigue la lista, y en un item vacio la cierra
 *   Shift+Enter          salto de linea sin cambiar de parrafo
 */

// Etiquetas que el navegador usa como bloque dentro de un contentEditable.
const BLOQUES = ["div", "p", "li"];

/**
 * Sube desde donde esta el cursor hasta el bloque que lo contiene.
 * Si el texto esta suelto directo en la raiz, el bloque es la raiz.
 */
const bloqueDe = (nodo, raiz) => {
  let n = nodo;

  while (n && n !== raiz) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      if (BLOQUES.includes(n.tagName.toLowerCase())) return n;
    }
    n = n.parentNode;
  }

  return raiz;
};

/**
 * Dos formas de abrir una lista, porque la gente escribe de las dos:
 *
 *   CON_ESPACIO  "- " o "1. "  -> convierte en cuanto se teclea el espacio
 *   SOLO         "-" o "1."    -> convierte al dar Enter
 *
 * La segunda hace falta de verdad: quien escribe un guion y aprieta Enter
 * espera que aparezca la lista, y con la regla del espacio se quedaba con un
 * guion suelto y una linea nueva.
 */
const MARCADOR_CON_ESPACIO = /^\s*([-*]|\d+[.)])\s$/;
const MARCADOR_SOLO = /^\s*([-*]|\d+[.)])\s*$/;

function EditorTexto({
  label,
  name,
  value,
  onChange,
  disabled,
  placeholder,
  className = "",
}) {
  const areaRef = useRef(null);

  // Lo ultimo que este componente emitio. Sirve para distinguir un cambio
  // propio (no hay que repintar) de uno externo (si hay que repintar).
  const propioRef = useRef(null);

  const [enfocado, setEnfocado] = useState(false);
  const [formatoActivo, setFormatoActivo] = useState({
    negrita: false,
    cursiva: false,
    lista: false,
    listaNum: false,
  });

  // Pinta el valor solo cuando viene de afuera. Reescribir el DOM en cada
  // tecla haria saltar el cursor al final.
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    if (value === propioRef.current) return;

    el.innerHTML = marcasAHtml(value);
    propioRef.current = value ?? "";
  }, [value]);

  const emitir = useCallback(() => {
    const el = areaRef.current;
    if (!el) return;

    const texto = htmlAMarcas(el);
    propioRef.current = texto;
    onChange?.({ target: { name, value: texto } });
  }, [name, onChange]);

  // Refleja en los botones el formato donde esta el cursor.
  const revisarFormato = useCallback(() => {
    if (disabled) return;
    try {
      setFormatoActivo({
        negrita: document.queryCommandState("bold"),
        cursiva: document.queryCommandState("italic"),
        lista: document.queryCommandState("insertUnorderedList"),
        listaNum: document.queryCommandState("insertOrderedList"),
      });
    } catch {
      // queryCommandState puede fallar si no hay seleccion; no importa.
    }
  }, [disabled]);

  const comando = (cmd) => {
    if (disabled) return;

    const el = areaRef.current;
    if (!el) return;

    el.focus();
    document.execCommand(cmd, false, null);

    revisarFormato();
    emitir();
  };

  /**
   * Convierte un marcador recien escrito en una lista de verdad.
   *
   * La condicion es que el bloque contenga UNICAMENTE el marcador. Con eso,
   * un guion a mitad de una frase ("filtro P-51 - modelo") no dispara nada, y
   * tampoco lo hace un precio como "1.5 kg".
   *
   * El marcador no se borra a mano nodo por nodo: se selecciona el bloque y
   * se usa execCommand, asi el navegador se encarga del cursor. Hacerlo a
   * mano dejaba el caret en un nodo que ya no existia.
   */
  const convertirEnLista = (patron) => {
    const el = areaRef.current;
    if (!el) return false;

    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false;

    const bloque = bloqueDe(sel.anchorNode, el);
    if (!bloque) return false;

    // Dentro de una lista ya no hay nada que abrir: ahi un guion es un guion.
    if (bloque.nodeType === Node.ELEMENT_NODE && bloque.closest("li")) {
      return false;
    }

    // Al escribir un espacio al final de una linea el navegador mete un
    // espacio duro. Va con el escape y no con el caracter suelto: invisible
    // en el archivo, cualquiera lo borraria sin notarlo.
    const texto = bloque.textContent.replace(/\u00A0/g, " ");
    const m = texto.match(patron);
    if (!m) return false;

    const cmd = /^\d/.test(m[1])
      ? "insertOrderedList"
      : "insertUnorderedList";

    const rango = document.createRange();
    rango.selectNodeContents(bloque);
    sel.removeAllRanges();
    sel.addRange(rango);

    document.execCommand("delete");
    document.execCommand(cmd, false, null);

    return true;
  };

  const alEscribir = () => {
    if (convertirEnLista(MARCADOR_CON_ESPACIO)) revisarFormato();
    emitir();
  };

  // Pegar entra como texto plano: si no, el navegador inserta el HTML del
  // origen (Word, otra pagina) con estilos y etiquetas que no queremos.
  const alPegar = (e) => {
    e.preventDefault();
    const texto = e.clipboardData?.getData("text/plain") ?? "";
    document.execCommand("insertText", false, texto);
    emitir();
  };

  const alTeclear = (e) => {
    if (e.ctrlKey || e.metaKey) {
      const k = e.key.toLowerCase();
      if (k === "b") {
        e.preventDefault();
        comando("bold");
        return;
      }
      if (k === "i") {
        e.preventDefault();
        comando("italic");
        return;
      }
    }

    // Enter sobre una linea que solo tiene el marcador: abre la lista en vez
    // de bajar de linea. Se corta el Enter con preventDefault porque el
    // cursor tiene que quedar DENTRO del item nuevo, no en una linea
    // siguiente que ya no hace falta.
    //
    // Shift+Enter no entra aca: ese es un salto de linea a proposito.
    if (e.key === "Enter" && !e.shiftKey) {
      if (convertirEnLista(MARCADOR_SOLO)) {
        e.preventDefault();
        revisarFormato();
        emitir();
        return;
      }
    }

    revisarFormato();
  };

  const vacio = !String(value ?? "").trim();

  const boton = (cfg) => (
    <button
      type="button"
      className={`edt-btn ${cfg.clase || ""} ${cfg.on ? "edt-btn--on" : ""}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => comando(cfg.cmd)}
      disabled={disabled}
      title={cfg.titulo}
    >
      {cfg.texto}
    </button>
  );

  return (
    <div className={`edt ${className}`}>
      <div className="edt-barra">
        {label ? <label className="edt-label">{label}</label> : <span />}

        <div className="edt-acciones">
          {boton({
            cmd: "bold",
            texto: "B",
            clase: "edt-btn--negrita",
            on: formatoActivo.negrita,
            titulo: "Bold (Ctrl+B)",
          })}

          {boton({
            cmd: "italic",
            texto: "I",
            clase: "edt-btn--cursiva",
            on: formatoActivo.cursiva,
            titulo: "Italic (Ctrl+I)",
          })}

          {boton({
            cmd: "insertUnorderedList",
            texto: "• List",
            on: formatoActivo.lista,
            titulo: 'Bullet list  (or type "- " at the start of a line)',
          })}

          {boton({
            cmd: "insertOrderedList",
            texto: "1. List",
            on: formatoActivo.listaNum,
            titulo: 'Numbered list  (or type "1. " at the start of a line)',
          })}
        </div>
      </div>

      <div className="edt-caja">
        <div
          ref={areaRef}
          className={`edt-area ${disabled ? "edt-area--off" : ""}`}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label || name}
          onInput={alEscribir}
          onBlur={() => {
            setEnfocado(false);
            emitir();
          }}
          onFocus={() => {
            setEnfocado(true);
            revisarFormato();
          }}
          onKeyUp={revisarFormato}
          onMouseUp={revisarFormato}
          onKeyDown={alTeclear}
          onPaste={alPegar}
        />

        {vacio && !enfocado && placeholder ? (
          <span className="edt-placeholder">{placeholder}</span>
        ) : null}
      </div>
    </div>
  );
}

export default EditorTexto;
